import classnames from "classnames";
import Icons from "@components/icons.ts";
import { Filters } from "../Filters";
import { ComboboxGroupedButtons } from "@components/ComboBoxes/ComboboxButtonGroup";
import { ComboboxGroupButton } from "@components/ComboBoxes/ComboboxButton";
import {
  EmailCaptureModal,
  FilterTabs,
  InsuranceFilterButton,
  NeedsFilterButton,
  StateFilterButton
} from "./components";
import { useEffect, useState, useCallback, useMemo } from "preact/hooks";
import {
  useFilterStateSync,
  useFilterSync,
  useTypeOfCareSync,
} from "./hooks";
import { getUrlParameter } from "./utils/getUrlParameter";
import { getValidationErrors } from "../utils/validationUtils";
import type { FilterComponentGroupProps } from "./types";
import "./styles/index.scss";

export const FilterComponentGroup = ({
  breakpoint = 'md',
  centeredFilters,
  email_capture_form,
  enable_email_capture_modal = false,
  filter_buttons_props,
  insurance,
  isMobile: propIsMobile,
  new_styles,
  optional_filter_props,
  preSelectedLocation,
  preSelectedInsurance,
  preSelectedSpecialties,
  rebrand,
  specialties: specialty,
  states: statesFilters,
  tabbed_filters,
  type_of_care,
  type_of_care_filter_tabs,
}: FilterComponentGroupProps) => {
  const [openEmailCaptureModal, setOpenEmailCaptureModal] = useState(false);

  // Sync all filter instances
  const {
    selectedItems: syncedSelectedItems,
    typeOfCare: syncedTypeOfCare,
    errors: syncedErrors,
    showValidationMessage,
    handleGetSelectedState: syncedHandleGetSelectedState,
    handleGetSelectedInsurance: syncedHandleGetSelectedInsurance,
    handleGetSelectedNeeds: syncedHandleGetSelectedNeeds,
    handleTypeOfCareChange: syncedHandleTypeOfCareChange,
    handleValidationUpdate: syncedHandleValidationUpdate,
    handleValidationMessageUpdate,
    syncedFields,
  } = useFilterSync();

  // Get the typeOfCare from the URL parameter
  const urlTypeOfCare = getUrlParameter('typeOfCare');

  // Sync the typeOfCare between instances
  const { typeOfCare, handleTypeOfCareChange } = useTypeOfCareSync({
    type_of_care,
    urlTypeOfCare,
    syncedTypeOfCare,
    syncedHandleTypeOfCareChange,
  });

  const searchButtonLabel = useMemo(() => (
    <div className='flex items-center w-full justify-center'>
      <Icons.SearchIcon />
      <span className='ComboboxButton__search-text ml-1'>Search</span>
    </div>
  ), []);

  return (
    <Filters
      email_capture_form={email_capture_form}
      specialties={specialty}
      states={statesFilters}
      insurance={insurance}
      optional_filter_props={optional_filter_props}
      filter_buttons_props={filter_buttons_props}
      type_of_care={typeOfCare}
      preSelectedLocation={preSelectedLocation}
      preSelectedInsurance={preSelectedInsurance}
      preSelectedSpecialties={preSelectedSpecialties}
    >
      {({
        selectedItems,
        handleRedirectClick,
        getRedirectUrl,
        requiredFiltersAllValid,
        selectLater,
        handleSelectLater,
        isMobile: filterIsMobile,
        insurance,
        specialty,
        statesFilters,
        handleOpenStates,
        handleOpenInsurance,
        handleOpenNeeds,
        handleGetSelectedInsurance,
        handleGetSelectedState,
        openInsurance,
        openNeeds,
        openStates,
        handleGetSelectedNeeds,
        errors,
      }) => {
        const isMobile = useMemo(() => propIsMobile ?? filterIsMobile, [propIsMobile, filterIsMobile]);

        const {
          combinedSelectedItems,
          combinedErrors,
          combinedHandleGetSelectedState,
          combinedHandleGetSelectedInsurance,
          combinedHandleGetSelectedNeeds,
        } = useFilterStateSync({
          selectedItems,
          syncedSelectedItems,
          syncedFields,
          errors,
          syncedErrors,
          handleGetSelectedState,
          handleGetSelectedInsurance,
          handleGetSelectedNeeds,
          syncedHandleGetSelectedState,
          syncedHandleGetSelectedInsurance,
          syncedHandleGetSelectedNeeds,
          syncedHandleValidationUpdate,
        });

        const { stateError, insuranceError } = getValidationErrors(showValidationMessage, combinedErrors);

        // Handle insurance dropdown
        const handleOpenInsuranceDropdown = useCallback((event: CustomEvent) => {
          const filterModalDialog = document.querySelector('[data-filter-modal-dialog]');
          if (!filterModalDialog) return;

          // Find the insurance button inside the filter modal
          const insuranceButton = filterModalDialog.querySelector('[data-combobox-button="insurance"]');
          if (insuranceButton instanceof HTMLElement) {
            const clickEvent = new MouseEvent('click', {
              bubbles: false,
              cancelable: true
            });
            insuranceButton.dispatchEvent(clickEvent);
          }
        }, []);

        useEffect(() => {
          document.addEventListener('open-filter-modal-insurance-filters', handleOpenInsuranceDropdown as EventListener);

          return () => {
            document.removeEventListener('open-filter-modal-insurance-filters', handleOpenInsuranceDropdown as EventListener);
          };
        }, [handleOpenInsuranceDropdown]);

        // Handle specialty badge selection - single-select behavior
        useEffect(() => {
          const handleSpecialtyBadgeSelection = (event: CustomEvent) => {
            const specialtyValue = event.detail.value;
            const selectedSpecialty = specialty.find(
              (_specialty) => _specialty.value.toLowerCase() === specialtyValue.toLowerCase()
            );
            
            if (selectedSpecialty) {
              // Clear existing specialty selections and set only the new one (single-select behavior)
              // This replaces any existing specialty selections with just the new one
              syncedHandleGetSelectedNeeds([selectedSpecialty]);
            }
          };

          document.addEventListener('specialty-badge-selected', handleSpecialtyBadgeSelection as EventListener);
          
          return () => {
            document.removeEventListener('specialty-badge-selected', handleSpecialtyBadgeSelection as EventListener);
          };
        }, [specialty, syncedHandleGetSelectedNeeds]);

        const handleSearchClick = useCallback((e: Event) => {
          e.preventDefault(); // Prevent default form submission
          
          // Always show validation messages when search is attempted
          handleValidationMessageUpdate(true);

          if (enable_email_capture_modal && email_capture_form && requiredFiltersAllValid?.()) {
            setOpenEmailCaptureModal(true);
            document.body.style.overflow = 'hidden';
          } else if (requiredFiltersAllValid?.()) {
            // Only redirect if filters are valid
            handleRedirectClick?.(e);
          }
          // If filters are not valid, validation messages will be shown but no redirect
        }, [enable_email_capture_modal, email_capture_form, requiredFiltersAllValid, handleRedirectClick, handleValidationMessageUpdate]);

        return (
          <form 
            className={classnames(
              "filter-component-group relative w-full md:w-fit", {
              "filter-component-group--centered-md": centeredFilters && breakpoint === 'md',
              "filter-component-group--centered-desktop": centeredFilters && breakpoint === 'desktop',
              "filter-component-group--centered-with-tabs-md": centeredFilters && tabbed_filters && breakpoint === 'md',
              "filter-component-group--centered-with-tabs-desktop": centeredFilters && tabbed_filters && breakpoint === 'desktop',
              "filter-component-group--tabbed-filters": tabbed_filters,
              "filter-component-group--rebrand": rebrand,
            })}
          >
            <FilterTabs
              breakpoint={breakpoint}
              centeredFilters={centeredFilters}
              onTypeOfCareChange={handleTypeOfCareChange}
              rebrand={rebrand}
              tabbed_filters={tabbed_filters}
              typeOfCare={typeOfCare}
              type_of_care_filter_tabs={type_of_care_filter_tabs}
            />
            <ComboboxGroupedButtons
              breakpoint={breakpoint}
              centered={centeredFilters}
              new_styles={new_styles}
              rebrand={rebrand}
              tabbed_filters={tabbed_filters}
            >
              <StateFilterButton
                breakpoint={breakpoint}
                centeredFilters={centeredFilters}
                combinedSelectedItems={combinedSelectedItems}
                error={stateError}
                filter_buttons_props={filter_buttons_props}
                handleGetSelectedItem={combinedHandleGetSelectedState}
                handleOpenClick={handleOpenStates}
                insurance={insurance}
                isMobile={isMobile}
                new_styles={new_styles}
                open={openStates}
                optional={filter_buttons_props?.[0]?.is_optional}
                rebrand={rebrand}
                selectedItem={combinedSelectedItems.state}
                statesFilters={statesFilters}
                tabbedFilters={tabbed_filters}
              />

              <InsuranceFilterButton
                breakpoint={breakpoint}
                centeredFilters={centeredFilters}
                combinedSelectedItems={combinedSelectedItems}
                error={insuranceError}
                filter_buttons_props={filter_buttons_props}
                handleGetSelectedItem={combinedHandleGetSelectedInsurance}
                handleOpenClick={handleOpenInsurance}
                insurance={insurance}
                isMobile={isMobile}
                new_styles={new_styles}
                open={openInsurance}
                optional={filter_buttons_props?.[1]?.is_optional}
                rebrand={rebrand}
                selectedItem={combinedSelectedItems.insurance}
                tabbedFilters={tabbed_filters}
              />

              <NeedsFilterButton
                breakpoint={breakpoint}
                centeredFilters={centeredFilters}
                filter_buttons_props={filter_buttons_props}
                handleGetSelectedItems={combinedHandleGetSelectedNeeds}
                handleOpenClick={handleOpenNeeds}
                handleSelectLater={handleSelectLater}
                isMobile={isMobile}
                open={openNeeds}
                optional={filter_buttons_props?.[2]?.is_optional}
                rebrand={rebrand}
                selectedItems={combinedSelectedItems?.specialty}
                selectLater={selectLater}
                specialties={specialty}
                tabbedFilters={tabbed_filters}
              />

              <ComboboxGroupButton
                breakpoint={breakpoint}
                button='submit'
                buttonWrapperClass='border-gt-black w-11/12 rounded-e-lg md:border-t md:border-b md:border-r md:border-l-0 my-4 md:my-0'
                centeredFilters={centeredFilters}
                cypressTestAttribute='submit'
                id="filter-search-button"
                label={searchButtonLabel}
                new_styles={new_styles}
                onClick={handleSearchClick}
                open={openEmailCaptureModal}
                rebrand={rebrand}
                tabbedFilters={tabbed_filters}
                variant='contained'
              >
                {({ open }) => (
                  <EmailCaptureModal
                    email_capture_form={email_capture_form}
                    openEmailCaptureModal={openEmailCaptureModal}
                    setOpenEmailCaptureModal={setOpenEmailCaptureModal}
                    showValidationMessage={showValidationMessage}
                    setShowValidationMessage={handleValidationMessageUpdate}
                    combinedSelectedItems={combinedSelectedItems}
                    getRedirectUrl={getRedirectUrl}
                    handleRedirectClick={handleRedirectClick}
                  />
                )}
              </ComboboxGroupButton>
            </ComboboxGroupedButtons>
          </form>
        );
      }}
    </Filters>
  );
};
